import { useGetAccountInsightsSummaryQuery } from "@api/accountManagement";
import { useMemo } from "react";
import { formatMoney } from "@pages/profile-v2/utils";
import {
	CHART_COLORS,
	MIN_SLICE_RATIO,
	formatDurationTooltip,
	getBaseChartOptions,
	getVisualSliceValue,
} from "../../utils";

const BALANCE_ROWS = [
	{ key: "real", labelKey: "accountInsights.balance.real" },
	{ key: "bonus", labelKey: "accountInsights.balance.bonus" },
	{ key: "cash", labelKey: "accountInsights.balance.cash" },
	{ key: "total", labelKey: "accountInsights.balance.total" },
];

const useController = ({ translate }) => {
	const summaryQuery = useGetAccountInsightsSummaryQuery();

	const balanceRows = useMemo(() => {
		const rows = [...BALANCE_ROWS];

		if (summaryQuery.data?.balance?.provision?.value) {
			rows.push({ key: "provision", labelKey: "accountInsights.balance.provision" });
		}

		return rows;
	}, [summaryQuery.data?.balance]);

	const balanceSegments = useMemo(() => {
		const balance = summaryQuery.data?.balance;

		if (!balance) {
			return [];
		}

		return [
			{
				key: "real",
				label: translate("accountInsights.balance.real"),
				value: Number(balance.real?.value) || 0,
				color: CHART_COLORS.primary,
			},
			{
				key: "bonus",
				label: translate("accountInsights.balance.bonus"),
				value: Number(balance.bonus?.value) || 0,
				color: CHART_COLORS.secondary,
			},
			{
				key: "cash",
				label: translate("accountInsights.balance.cash"),
				value: Number(balance.cash?.value) || 0,
				color: CHART_COLORS.tertiary,
			},
		].filter((segment) => segment.value > 0);
	}, [summaryQuery.data?.balance, translate]);

	const balanceCurrency =
		summaryQuery.data?.balance?.total?.currency ||
		summaryQuery.data?.balance?.real?.currency;

	const balanceTotalValue = useMemo(
		() => balanceSegments.reduce((sum, segment) => sum + segment.value, 0),
		[balanceSegments]
	);

	const summaryUsageChart = useMemo(
		() => ({
			data: {
				labels: [
					translate("accountInsights.timeOfUse.today"),
					translate("accountInsights.timeOfUse.last7Days"),
					translate("accountInsights.timeOfUse.last30Days"),
				],
				datasets: [
					{
						label: translate("accountInsights.sessionActivity.chartLabel"),
						data: [
							Math.round(
								(summaryQuery.data?.session?.totalDurationTodaySeconds || 0) / 60
							),
							Math.round(
								(summaryQuery.data?.session?.totalDurationLast7DaysSeconds || 0) / 60
							),
							Math.round(
								(summaryQuery.data?.session?.totalDurationLast30DaysSeconds || 0) / 60
							),
						],
						backgroundColor: [
							CHART_COLORS.primary,
							CHART_COLORS.purple,
							CHART_COLORS.secondary,
						],
						borderRadius: 8,
						borderSkipped: false,
						maxBarThickness: 56,
					},
				],
			},
			options: {
				...getBaseChartOptions(),
				plugins: {
					...getBaseChartOptions().plugins,
					legend: { display: false },
					tooltip: {
						...getBaseChartOptions().plugins.tooltip,
						callbacks: {
							label: (context) => formatDurationTooltip(context.parsed.y),
						},
					},
				},
			},
		}),
		[summaryQuery.data?.session, translate]
	);

	const balanceChart = useMemo(() => {
		if (!balanceSegments.length) {
			return null;
		}

		return {
			data: {
				labels: balanceSegments.map((segment) => segment.label),
				datasets: [
					{
						data: balanceSegments.map((segment) =>
							getVisualSliceValue(segment.value, balanceTotalValue)
						),
						backgroundColor: balanceSegments.map((segment) => segment.color),
						borderColor: "rgba(22, 24, 35, 0.85)",
						borderWidth: 2,
						hoverBorderColor: "#FCFCFC",
						hoverBorderWidth: 2,
						offset: balanceSegments.map((segment) => {
							const ratio = segment.value / balanceTotalValue;
							return ratio < MIN_SLICE_RATIO ? 10 : 0;
						}),
					},
				],
			},
			options: {
				responsive: true,
				maintainAspectRatio: false,
				cutout: "62%",
				plugins: {
					legend: { display: false },
					tooltip: {
						backgroundColor: CHART_COLORS.tooltipBg,
						titleColor: "#FCFCFC",
						bodyColor: CHART_COLORS.text,
						borderColor: "rgba(110, 113, 145, 0.35)",
						borderWidth: 1,
						padding: 12,
						cornerRadius: 10,
						callbacks: {
							label: (context) => {
								const segment = balanceSegments[context.dataIndex];
								const percent = balanceTotalValue
									? ((segment.value / balanceTotalValue) * 100).toFixed(1)
									: "0";

								return `${formatMoney(segment?.value, balanceCurrency)} (${percent}%)`;
							},
						},
					},
				},
			},
		};
	}, [balanceSegments, balanceTotalValue, balanceCurrency]);

	return {
		state: {
			summary: summaryQuery.data,
			isLoading: summaryQuery.isLoading,
			isError: summaryQuery.isError,
			balanceRows,
			balanceSegments,
			balanceCurrency,
			summaryUsageChart,
			balanceChart,
		},
		handlers: {
			refetch: summaryQuery.refetch,
		},
	};
};

export default useController;
